import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import {
  cloneDataOrNull,
  DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData,
} from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { TicketTemplate } from './ticket-template';
import { TimeZoneRegion } from './time-zone-region.js';
import { ContactDetails } from './contact-details.js';
import { Country } from './country.js';
import { TrueTime } from '../../time/true-time.js';
import { CustomerProfile } from './customer-profile.js';

export enum ThirdPartyEventTicketStatus {
  Live = 'LIVE',
  Scanned = 'SCANNED',
}

export class ThirdPartyEventTicket extends DataModel {
  static readonly TYPE_ID: string = '3f8f9e66ecac4b68a56c6cbf774376f8';

  public readonly customerId: string | null;
  public readonly templateId: string;

  private constructor(
    public readonly uniqueId: string,
    public readonly scanCode: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly country: Country,
    public readonly timezone: TimeZoneRegion,
    public readonly businessRef: BusinessModuleRef,
    public readonly status: ThirdPartyEventTicketStatus,
    public readonly eventId: string,
    public readonly template: TicketTemplate,
    public readonly contactDetails: ContactDetails,
    public readonly scanDate: dayjs.Dayjs | null,
    public readonly scannerId: string | null,
    public readonly customer: CustomerProfile | null,
  ) {
    super();
    this.templateId = template.uniqueId;
    this.customerId = customer?.uniqueId ?? null;
  }

  get $typeId(): string {
    return ThirdPartyEventTicket.TYPE_ID;
  }

  static create(opts: {
    uniqueId: string,
    scanCode: string,
    creationDate: dayjs.Dayjs,
    country: Country,
    timezone: TimeZoneRegion,
    businessRef: BusinessModuleRef,
    status: ThirdPartyEventTicketStatus,
    eventId: string,
    template: TicketTemplate,
    contactDetails: ContactDetails,
    customer: CustomerProfile | null,
  }): ThirdPartyEventTicket {
    return new ThirdPartyEventTicket(
        opts.uniqueId,
        opts.scanCode,
        opts.creationDate,
        opts.country,
        opts.timezone,
        opts.businessRef,
        opts.status,
        opts.eventId,
        opts.template,
        opts.contactDetails,
        null,
        null,
        opts.customer,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): ThirdPartyEventTicket {
    return new ThirdPartyEventTicket(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'scanCode'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'country'), Country),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        toEnumFromString(MapUtils.get<string>(map, '$status'), ThirdPartyEventTicketStatus),
        MapUtils.get<string>(map, 'eventId'),
        context.fromData<TicketTemplate>(MapUtils.get<SerializedData>(map, 'template')),
        context.fromData<ContactDetails>(MapUtils.get<SerializedData>(map, 'contactDetails')),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'scanDate')),
        MapUtils.getOrNull<string>(map, 'scannerId'),
        context.fromDataOrNull<CustomerProfile>(MapUtils.getOrNull<SerializedData>(map, 'customer')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'scanCode': this.scanCode,
      'creationDate': context.encodeDate(this.creationDate),
      'country': fromEnumAsString(this.country),
      'timezone': fromEnumAsString(this.timezone),
      'businessRef': this.businessRef.encode(),
      '$status': fromEnumAsString(this.status),
      'eventId': this.eventId,
      'template': context.toData(this.template),
      'templateId': this.template.uniqueId,
      'contactDetails': context.toData(this.contactDetails),
      'scanDate': context.encodeDateOrNull(this.scanDate),
      'scannerId': this.scannerId ?? null,
      'customer': context.toDataOrNull(this.customer),
      'customerId': this.customerId,
    };
  }

  clone(): this {
    return new ThirdPartyEventTicket(
        this.uniqueId,
        this.scanCode,
        TimeZone.cloneDate(this.creationDate),
        this.country,
        this.timezone,
        this.businessRef.clone(),
        this.status,
        this.eventId,
        this.template.clone(),
        this.contactDetails.clone(),
        TimeZone.cloneDateOrNull(this.scanDate),
        this.scannerId,
        cloneDataOrNull(this.customer),
    ) as this;
  }

  isSame(other: ThirdPartyEventTicket): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.scanCode === this.scanCode &&
      other.creationDate === this.creationDate &&
      other.country === this.country &&
      other.timezone === this.timezone &&
      other.businessRef === this.businessRef &&
      other.status === this.status &&
      other.eventId === this.eventId &&
      isSameData(other.template, this.template) &&
      isSameData(other.contactDetails, this.contactDetails) &&
      isSameData(other.customer, this.customer) &&
      other.scanDate === this.scanDate &&
      other.scannerId === this.scannerId;
  }

  markAsScanned(scannerId: string): this {
    return new ThirdPartyEventTicket(
        this.uniqueId,
        this.scanCode,
        TimeZone.cloneDate(this.creationDate),
        this.country,
        this.timezone,
        this.businessRef.clone(),
        ThirdPartyEventTicketStatus.Scanned,
        this.eventId,
        this.template.clone(),
        this.contactDetails.clone(),
        TrueTime.now(),
        scannerId,
        cloneDataOrNull(this.customer),
    ) as this;
  }

  markAsRestored(): this {
    return new ThirdPartyEventTicket(
        this.uniqueId,
        this.scanCode,
        TimeZone.cloneDate(this.creationDate),
        this.country,
        this.timezone,
        this.businessRef.clone(),
        ThirdPartyEventTicketStatus.Live,
        this.eventId,
        this.template.clone(),
        this.contactDetails.clone(),
        null,
        null,
        cloneDataOrNull(this.customer),
    ) as this;
  }

  copyWithTicketTemplate(ticketTemplate: TicketTemplate): this {
    return new ThirdPartyEventTicket(
        this.uniqueId,
        this.scanCode,
        TimeZone.cloneDate(this.creationDate),
        this.country,
        this.timezone,
        this.businessRef.clone(),
        this.status,
        this.eventId,
        ticketTemplate,
        this.contactDetails.clone(),
        TimeZone.cloneDateOrNull(this.scanDate),
        this.scannerId,
        cloneDataOrNull(this.customer),
    ) as this;
  }

  static encodedStatusSafe(status: ThirdPartyEventTicketStatus): string {
    return fromEnumAsString(status);
  }
}

export class ThirdPartyEventTicketBuilder extends DataModelBuilder<ThirdPartyEventTicket> {
  build(context: SerializationContext, map: SerializedData): ThirdPartyEventTicket {
    return ThirdPartyEventTicket.fromMap(context, map);
  }
}
